import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_COLOR = "https://customer-assets.emergentagent.com/job_2f7e215b-21fb-4724-b9fe-01f573af4d89/artifacts/06a0r6yy_Tailwind%20logo-Full-C.png";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("admin_token", data.token);
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#182649] flex items-center justify-center px-6" style={{ fontFamily: "Manrope, sans-serif" }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <img src={LOGO_COLOR} alt="Tailwind Corporation" className="h-16 w-auto mx-auto mb-6" data-testid="login-logo" />
          <h1
            className="text-2xl font-bold text-white uppercase tracking-tight"
            style={{ fontFamily: "Outfit, sans-serif" }}
            data-testid="login-title"
          >
            Admin Access
          </h1>
          <p className="text-sm text-white/50 mt-2">Sign in to manage leads</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="admin@tailwindcorp.com"
              className="h-12 rounded-md bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#D49A38]"
              data-testid="login-email"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-white/70 text-sm">Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter password"
              className="h-12 rounded-md bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-[#D49A38]"
              data-testid="login-password"
            />
          </div>

          {error && (
            <p className="text-red-400 text-sm text-center" data-testid="login-error">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D49A38] text-white hover:bg-[#c08a2e] rounded-full px-8 py-6 text-base font-semibold transition-all duration-300"
            data-testid="login-submit"
          >
            {loading ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
            ) : (
              <><Lock className="mr-2 h-4 w-4" /> Sign In</>
            )}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <a href="/" className="text-sm text-white/30 hover:text-white/50 transition-colors" data-testid="login-back-link">
            Back to website
          </a>
        </div>
      </div>
    </div>
  );
}
